/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {CommonModule} from '@angular/common';
import {
  ApplicationRef,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
  Provider
} from '@angular/core';

import {TranslateModule} from '@ngx-translate/core';

import {IdleTimeoutModalComponent} from './../../shared/modal/idle-timeout-modal/idle-timeout-modal.component';
import {AlertModalComponent} from './alert-modal.component';
import {ApiErrorModalComponent} from './api-error-modal/api-error-modal.component';
import {ConfirmModalComponent} from './confirm-modal.component';
import {DialogModalComponent} from './dialog-modal.component';
import {DynamicConfirmModalComponent} from './dynamic-confirm-modal.component';
import {ModalDefinition, Modals} from './interfaces';
import {ModalBackdropComponent} from './modal-backdrop.component';
import {ModalOutletComponent} from './modal-outlet.component';
import {ModalStructuralDirective} from './modal-structural.directive';
import {ModalComponent} from './modal.component';
import {ModalCloseDirective, ModalDirective} from './modal.directive';
import {ModalService} from './modal.service';
import {OpenModalDirective} from './open-modal.directive';

import {ClipboardModule} from 'ngx-clipboard';
import {UiModule} from '../ui/ui.module';

export const MODALS = new InjectionToken('MODALS');

export const MODAL_PROVIDERS: Provider[] = [
  {
    provide: ModalService,
    useFactory: setupModals,
    deps: [
      ApplicationRef, MODALS
    ]
  }
];

@NgModule({
  imports: [
    CommonModule,
    TranslateModule,
    UiModule,
    ClipboardModule
  ],
  declarations: [
    AlertModalComponent,
    ConfirmModalComponent,
    DynamicConfirmModalComponent,
    DialogModalComponent,
    ApiErrorModalComponent,
    ModalBackdropComponent,
    ModalComponent,
    ModalOutletComponent,
    ModalDirective,
    ModalCloseDirective,
    OpenModalDirective,
    ModalStructuralDirective,
    IdleTimeoutModalComponent
  ],
  exports: [
    AlertModalComponent,
    ConfirmModalComponent,
    DynamicConfirmModalComponent,
    DialogModalComponent,
    ApiErrorModalComponent,
    ModalBackdropComponent,
    ModalComponent,
    ModalOutletComponent,
    ModalDirective,
    ModalCloseDirective,
    OpenModalDirective,
    ModalStructuralDirective
  ]
})
export class ModalModule {
  public static forRoot(modals: Modals): ModuleWithProviders<ModalModule> {
    return {
      ngModule: ModalModule,
      providers: [
        MODAL_PROVIDERS,
        provideModals(modals)
      ]
    };
  }
}

export function setupModals(ref: ApplicationRef, config: ModalDefinition[]) {
  if (ref.componentTypes.length === 0) {
    throw new Error('Bootstrap at least one component before injecting Modal service.');
  }
  return new ModalService(config);
}

export function provideModals(modals: Modals): any {
  return [
    {provide: MODALS, useValue: modals}
  ];
}
